/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Box, Checkbox, Grid, IconButton, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { APP_ACTION_TYPES, CONTROL_TABLE_PARAMETER_MAP, DISPLACEMENT_STEP_SIZE_DIVISION_COUNT, HIDE_TARGET_ECD, HYDRAULICS_FULL_ACCESS_PERMISSIONS, MAX_ROW_LIMITS } from '../../../../utils/constants';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import { COLORS } from '../../../../shared/config/colors';
import { addNewDisplacementRow, createDisplacements, deleteDisplacement, listDisplacement, updateDisplacement } from '../../../../store/actions/hydraulicsDisplacement';
import { displacementTablecColumns } from './table_config';
import SelectDrillDetails from '../../SelectDrillDetails';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import TargetModal from '../TargetModal';
import { checkPermissions } from '../../../../utils/checkPermissions';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const DisplacementModal = ({
  selectedJob,
  rowData,
  setAccordionProperty,
  proxyLoader
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const hydraulicsDisplacement = useSelector((state) => state.displacement);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  const observation = useSelector((state) => state.observation);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState('None');
  const [targetECD, setTargetECD] = useState('');
  const [targetData, setTargetData] = useState({});
  const [selectedRow, setSelectedRow] = useState({});

  const trackIdsRef = useRef([]);

  useEffect(() => {
    if (!hydraulicsDisplacement.loading) {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listDisplacement(payload));
    }
  }, []);

  useEffect(() => {
    if (hydraulicsDisplacement?.data?.length && !hydraulics?.drillDetails?.loading) {
      const { drillFluids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      const units = hydraulics?.drillDetails?.units?.MD || { decimals: 2, label: 'm' };
      const formatFluid = (id) => {
        const fluid = drillFluids.find((item) => item._id === id);
        return fluid
          ? `${fluid.name.trim()} @ ${fluid.MD.toFixed(units.decimals)}${units.label}`
          : '';
      };
      const updatedProperties = hydraulicsDisplacement?.data?.map((item, index) => {
        const hasStepSize = item?.step_size !== undefined && item?.step_size !== null;
        const drillFluid = drillFluids.find((detail) => detail._id === item?.fluid_id);
        if (!drillFluid && !properties?.length) {
          setInvalidRows(prev => ({ ...prev, [item._id]: true }));
          dispatch(
            enqueueSnackbar(
              'Configured Drillfluid in Displacement is not found, Please Check',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        }

        const updatedDetail = {
          ...item,
          step_size: hasStepSize ? item.step_size : 100,
          fluid_id: item?.isNewRow
            ? ''
            : item?.fluid_id
              ? formatFluid(item.fluid_id)
              : '',
          row: index + 1,
          ...(hasStepSize ? {} : { isDirty: true }),
        };

        if (!drillFluid) {
          updatedDetail.isDirty = true;
        }

        return updatedDetail;
      });
      const updateTrackFields = (data) => {
        return data.map((item) => ({
          ...item,
          track: item?.track ?? false,
        }));
      };

      const updatedPropertiesWithTrack = updateTrackFields(updatedProperties);
      setProperties(updatedPropertiesWithTrack);
      setAccordionProperty(updatedPropertiesWithTrack);
      trackIdsRef.current = updatedPropertiesWithTrack.filter(item => item?.track).map(item => item._id);
    } else {
      setProperties([]);
      setAccordionProperty([]);
    }
  }, [hydraulicsDisplacement?.data]);

  useEffect(() => {
    if (properties[0]?.from === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'flow_in', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'flow_in');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  useEffect(() => {
    if (targetData?.anchor_point && selectedRow?._id) {
      setProperties(prevProperties => {
        const updatedProperties = prevProperties.map(item =>
          item._id === selectedRow._id
            ? {
                ...item,
                target_data: targetData,
                isDirty: true
              }
            : item
        );
        setAccordionProperty(updatedProperties);
        return updatedProperties;
      });
    }
  }, [targetData]);

  const handleAddParameter = () => {
    dispatch(addNewDisplacementRow());
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty,
          };
        }
        return item;
      });
      setAccordionProperty(updatedProperties);
      return updatedProperties;
    });
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateDisplacementDetails = (details) => {
    const { _id, flow_in, fluid_volume, pumps_output, step_size } = details;
    const min_flow_in = hydraulicsDisplacement.units.inputs.flow_in.min;
    const max_flow_in = hydraulicsDisplacement.units.inputs.flow_in.max;
    const min_fluid_volume = hydraulicsDisplacement.units.inputs.fluid_volume.min;
    const min_pumps_output = hydraulicsDisplacement.units.inputs.pumps_output.min;
    const max_pumps_output = hydraulicsDisplacement.units.inputs.pumps_output.max;
    const min_step_size = ((fluid_volume / pumps_output) / DISPLACEMENT_STEP_SIZE_DIVISION_COUNT.min);
    const max_step_size = ((fluid_volume / pumps_output) / DISPLACEMENT_STEP_SIZE_DIVISION_COUNT.max);

    if (flow_in <= min_flow_in || flow_in > max_flow_in) {
      return showError(`Flow Rate must be between ${min_flow_in} and ${max_flow_in} ${hydraulicsDisplacement?.units?.inputs?.flow_in?.label}`, _id);
    }

    if (fluid_volume <= min_fluid_volume) {
      return showError(`Displacing Volume must be greater than ${min_fluid_volume} ${hydraulicsDisplacement?.units?.inputs?.fluid_volume?.label}`, _id);
    }

    if (pumps_output <= min_pumps_output || pumps_output > max_pumps_output) {
      return showError(`Total Pumps Output must be between ${min_pumps_output} and ${max_pumps_output} ${hydraulicsDisplacement?.units?.inputs?.pumps_output?.label}`, _id);
    }

    if (step_size <= min_step_size || step_size > max_step_size) {
      return showError(`Step Size must be between ${min_step_size.toFixed(0)} and ${max_step_size.toFixed(0)} ${hydraulicsDisplacement?.units?.inputs?.step_size?.label}`, _id);
    }

    return true;
  };

  const checkDisplacementValues = (value) => {
    const row = value;
    const invalidFields = [];
    const isInvalidNumber = (val) => {
      // eslint-disable-next-line no-mixed-operators
      return isNaN(val) || val === '' || typeof val === 'string' && val.trim() === '';
    };

    if (row?.flow_in === undefined || row?.flow_in === null || isInvalidNumber(row.flow_in)) {
      invalidFields.push('Flow Rate');
    }
    if (row?.fluid_id === undefined || row?.fluid_id === null || row?.fluid_id === '') invalidFields.push('Displacing Fluid');

    if (row?.fluid_volume === undefined || row?.fluid_volume === null || isInvalidNumber(row.fluid_volume)) {
      invalidFields.push('Displacing Volume');
    }
    if (row?.pumps_output === undefined || row?.pumps_output === null || isInvalidNumber(row.pumps_output)) {
      invalidFields.push('Total Pumps Output');
    }
    if (row?.step_size === undefined || row?.step_size === null || isInvalidNumber(row.step_size)) {
      invalidFields.push('Step Size');
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateDisplacementDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
                    `Please enter the following details: ${invalidFields.join(', ')}`,
                    'error',
                    new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const transformRunsECDSpan = (targetData, hydraulics) => {
    if (!targetData.ecd_span) return [];
    return targetData?.ecd_span?.map((row) => ({
      name: row?.name,
      low_emw: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.low_emw, hydraulics.units.inputs?.ecd_span?.low_emw?.conversionFactor) : Number(row.low_emw),
      high_emw: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.high_emw, hydraulics.units.inputs?.ecd_span?.high_emw?.conversionFactor) : Number(row.high_emw),
    }));
  };

  const transformControlsTable = (targetData, hydraulics) => {
    if (!targetData.controls_table) return [];
    return targetData?.controls_table?.map((row) => {
      const parameter = CONTROL_TABLE_PARAMETER_MAP[row?.parameter] || row?.parameter;
      return ({
        maintain: row?.maintain?.toLowerCase(),
        parameter,
        from: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.from, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.from),
        to: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.to, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.to),
        step_size: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.step_size, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.step_size),
      });
    });
  };

  const saveDisplacement = (row) => {
    if (checkDisplacementValues(row)) {
      const { drillFluids } = hydraulics.drillDetails.hydraulicsDrillDetails;
      const units = hydraulics?.drillDetails?.units?.MD || { decimals: 2, label: 'm' };

      const fluidMap = drillFluids.reduce((map, item) => {
        const key = `${item?.name?.trim()} @ ${item?.MD.toFixed(units.decimals)}${units.label}`;
        map[key] = item?._id;
        return map;
      }, {});

      const body = {
        flow_in: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.flow_in, hydraulicsDisplacement?.units?.inputs?.flow_in?.conversionFactor) : Number(row.flow_in),
        fluid_volume: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.fluid_volume, hydraulicsDisplacement?.units?.inputs?.fluid_volume?.conversionFactor) : Number(row.fluid_volume),
        pumps_output: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.pumps_output, hydraulicsDisplacement?.units?.inputs?.pumps_output?.conversionFactor) : Number(row.pumps_output),
        fluid_id: fluidMap[row.fluid_id] || null,
        step_size: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.step_size, hydraulicsDisplacement?.units?.inputs?.step_size?.conversionFactor) : Number(row.step_size),
        track: row?.track ?? false
      };

      if (row?.target_data && Object.keys(row?.target_data).length !== 0) {
        const updatedRunsECDSpan = transformRunsECDSpan(row?.target_data, hydraulics);
        const updatedControlsTable = transformControlsTable(row?.target_data, hydraulics);

        body.target_data = {
          anchor_point: row?.target_data?.anchor_point,
          target_ecd: row?.target_data?.target_ecd == null
            ? null
            : unit !== UNIT_SYSTEM.US
              ? convertUnitSystem(
                row?.target_data?.target_ecd,
                hydraulics.units.inputs?.target_data?.target_ecd?.conversionFactor
              )
              : Number(row?.target_data?.target_ecd),
          ecd_span: updatedRunsECDSpan,
          controls_table: updatedControlsTable
        };
      }

      const payload = {
        inputs: body,
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        ...(row?.isNewRow ? {} : { _id: row._id })
      };
      const hydraulicsBody = {
        isDisplacementResults: rowData?.isDisplacementResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      if (row?.isNewRow) {
        dispatch(createDisplacements(payload, hydraulicsBody));
      } else {
        dispatch(updateDisplacement(payload, hydraulicsBody));
      }
      setAnchorPoint('None');
      setTargetECD(null);
      setTargetData({});
    }
  };

  const removeDisplacement = (row) => {
    if (row?.isNewRow) {
      const updatedParams = properties.filter(item => item._id !== row?._id);
      setProperties(updatedParams);
      setAccordionProperty(updatedParams);
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listDisplacement(payload));
    } else {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        _id: row._id
      };
      const hydraulicsBody = {
        isDisplacementResults: rowData?.isDisplacementResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      dispatch(deleteDisplacement(payload, hydraulicsBody));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  const copyDisplacement = (row) => {
    setProperties((prev) => {
      const newRow = {
        ...row,
        track: false,
        row: prev.length + 1,
        _id: generateRandom(),
        isNewRow: true,
        isDirty: true,
      };
      const updated = [...prev, newRow];
      setAccordionProperty(updated);
      return updated;
    });
  };

  const renderActionCell = ({
    row
  }) => {
    return (
            <Grid container justifyContent="center">
                <IconButton
                    variant="solid"
                    disabled={!row.isDirty || hydraulicsDisplacement.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulicsDisplacement?.permissions))}
                    onClick={() => {
                      saveDisplacement(row);
                    }}
                >
                    <SaveRounded />
                </IconButton>
                  <IconButton
                        variant="solid"
                        disabled={hydraulics?.details?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.CREATE, hydraulicsDisplacement?.permissions)) || hydraulicsDisplacement?.loading || properties.some(item => item.isDirty === true || item.isNewRow || properties?.length === MAX_ROW_LIMITS.DISPLACEMENT)}
                        onClick={() => {
                          copyDisplacement(row);
                        }}
                      >
                        <ContentCopyOutlined />
                      </IconButton>
                <IconButton
                    variant="solid"
                    disabled={hydraulicsDisplacement.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, hydraulicsDisplacement?.permissions))}
                    onClick={() => {
                      setDeleteAction(true);
                      setDeleteRow(row);
                    }}
                >
                    <DeleteRounded />
                </IconButton>
            </Grid>
    );
  };

  const renderTargetsEditAction = ({ row }) => {
    return (
        <Grid container justifyContent="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                variant="solid"
                disabled={observation.loading}
                onClick={() => {
                  if (row?.target_data?.anchor_point) {
                    setAnchorPoint(row?.target_data?.anchor_point);
                    setTargetECD(row?.target_data?.target_ecd);
                    setTargetData(row?.target_data);
                  }
                  setShowTargetModal(true);
                  dispatch({
                    type: REDUX_ACTIONS.OBSERVATION_LIST_LOADING,
                  });
                  setTimeout(() => {
                    setSelectedRow(row);
                  }, 100);
                }}
              >
                <EditOutlined />
              </IconButton>
              {!row?.target_data?.anchor_point || HIDE_TARGET_ECD.includes(row?.target_data?.anchor_point)
                ? (
                  <Typography variant="button" fontSize={'1rem'} fontWeight={900}>None</Typography>
                  )
                : (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                      {row?.target_data?.target_ecd} {hydraulics.units.inputs.target_data.target_ecd.label}
                    </Typography>
                    <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                      @ {row?.target_data?.anchor_point}
                    </Typography>
                  </Box>
                  )}
            </Box>
          </Grid>
        </Grid>
    );
  };

  const renderTrackAction = ({ row }) => {
    const handleClick = (e) => {
      e.stopPropagation();

      const isChecked = e.target.checked;
      const isTracked = trackIdsRef.current.includes(row._id);
      const hasOtherTracked = properties.some(item => item.track);

      if (isChecked) {
        if (hasOtherTracked) {
          dispatch(
            enqueueSnackbar(
              'Only one row can get checked for tracking its fluid, please revise',
              'error',
              Date.now() + Math.random()
            )
          );
          return;
        }

        if (trackIdsRef.current.length && !isTracked) {
          dispatch(
            enqueueSnackbar(
              'Only one row can get checked for tracking its fluid, please save changes to proceed',
              'error',
              Date.now() + Math.random()
            )
          );
          return;
        }
      }

      updateData(row._id, 'track', isChecked);
    };

    return (
      <Box display="flex" justifyContent="center" width="100%">
        <Checkbox
          checked={!!row?.track}
          onClick={handleClick}
          disabled={row?.isNewRow}
        />
      </Box>
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderDrillFluidCell = (params) => {
    return (
            <SelectDrillDetails
                {...params}
                closeCellAfterSelect={true}
                updateData={updateData}
                data={hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillFluids}
                label={hydraulics?.drillDetails?.units?.MD?.label}
                decimals={hydraulics?.drillDetails?.units?.MD?.decimals}
            />
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
        <>
            <Grid container maxWidth={'100%'}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box display="flex" alignItems="center" marginLeft={'13px'}>
                        <Typography
                            variant="button"
                            fontWeight="700"
                            onClick={handleAddParameter}
                            className={
                                properties?.length === MAX_ROW_LIMITS.DISPLACEMENT || properties.some(item => item.isDirty === true) || hydraulicsDisplacement?.loading || hydraulics?.details?.loading || hydraulics?.details?.data[0]?.isNewRow ||
                                  !checkPermissions(APP_ACTION_TYPES.CREATE, hydraulicsDisplacement?.permissions)
                                  ? classes.disableIcon
                                  : ''
                            }
                            sx={{ cursor: 'pointer' }}
                        >
                            + Add
                        </Typography>

                        <Typography
                            variant="h6"
                            color={COLORS.LIGHT_GREY}
                            fontWeight="500"
                            sx={{ marginLeft: '12px' }}
                        >
                            {`(Note: Maximum ${MAX_ROW_LIMITS.DISPLACEMENT} entries are allowed)`}
                        </Typography>
                    </Box>
                </Box>
                <Grid item xs={12}>
                    <Datagrid
                        apiRef={apiRef}
                        data={properties}
                        sx={{
                          marginLeft: '-6px',
                          '.MuiDataGrid-cell': {
                            fontSize: '1rem',
                            fontWeight: 900,
                            textAlign: 'justify',
                            pointerEvents: proxyLoader ? 'none' : 'auto',
                            backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
                          },
                          '.MuiDataGrid-columnHeader': {
                            fontSize: '1.2rem !important',
                            fontWeight: '900 !important'
                          },
                        }}
                        loading={hydraulicsDisplacement.loading || hydraulics?.details?.loading}
                        columns={displacementTablecColumns(renderValueCell, renderDrillFluidCell, renderActionCell, hydraulicsDisplacement?.units?.inputs, unit, renderTargetsEditAction, renderTrackAction)}
                        onRowClick={(params, evt) => {
                          evt.preventDefault();
                          evt.stopPropagation();
                        }}
                        disableStickyHeader={false}
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        autoHeight={false}
                        getRowId={(row) => row._id}
                        getRowHeight={() => 'auto'}
                        height={'28vh'}
                        getRowClassName={(params) => {
                          return invalidRows[params.id] ? classes.invalidRow : '';
                        }}
                        isRowSelectable={() => !proxyLoader}
                        pageSize={100}
                        editMode="cell"
                        toolbar={{
                          options: {
                            columns: false,
                            filters: false,
                            density: false,
                            export: false
                          },
                        }}
                        enableDirtyLock
                        permissions={hydraulicsDisplacement?.permissions}
                    />
                </Grid>
            </Grid>
            {
                deleteAction && <DeleteConfirmationModal
                    open={deleteAction}
                    handleDelete={removeDisplacement}
                    handleClose={setDeleteAction}
                    deleteRow={deleteRow}
                />
            }
      {showTargetModal
        ? <TargetModal
          data={rowData}
          anchorPoint={anchorPoint}
          targetECD={targetECD}
          setTargetData={setTargetData}
          selectedJob={selectedJob}
          isOpen={showTargetModal}
          setAnchorPoint={setAnchorPoint}
          setTargetECD={setTargetECD}
          targetData={targetData}
          onSave={handleAnchorPointTargetECD}
          setParameterDirty={() => { }}
          onCloseClick={() => {
            setAnchorPoint('None');
            setTargetECD(null);
            setShowTargetModal(false);
            setTimeout(() => {
              setTargetData({});
              setSelectedRow({});
            }, 100);
          }}
          permissions={selectedRow?.isNewRow ? { permissions: HYDRAULICS_FULL_ACCESS_PERMISSIONS } : hydraulicsDisplacement}
        />
        : null
      }
        </>
  );
};
DisplacementModal.propTypes = {
  selectedJob: PropTypes.object,
  rowData: PropTypes.object,
  setAccordionProperty: PropTypes.func,
  proxyLoader: PropTypes.bool
};
DisplacementModal.defaultProps = {
  selectedJob: {}
};
export default DisplacementModal;
